"use client";

import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Certainty() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [italicSettled, setItalicSettled] = useState(false);
  useEffect(() => {
    if (isVisible) {
      const t = setTimeout(() => setItalicSettled(true), 600);
      return () => clearTimeout(t);
    }
  }, [isVisible]);
  const proofPoints = [{
    caption: "SECTOR EXPERTISE",
    title: "Hazardous industrial environments"
  }, {
    caption: "RISK MITIGATION",
    title: "Insurance-grade technical audits"
  }, {
    caption: "ENGINEERING RIGOR",
    title: "Any project size or location"
  }];
  return <section data-section-id="20305" id="engineering-certainty" className="bg-background text-foreground py-32 md:py-44 px-6 md:px-10 lg:px-16">
      <div ref={ref} className="mx-auto max-w-[1240px]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {}
          <div className="lg:col-span-7">
            {}
            <div className="transition-all duration-700 ease-out" style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              <p className="font-sans text-xs md:text-[0.8125rem] font-bold uppercase tracking-[0.15em] text-foreground/70">
                Fire Protection Engineering
              </p>
              <div className="mt-4 h-px w-12 bg-foreground/15" />
            </div>

            {}
            <h1 className="font-serif font-normal mt-10 text-foreground transition-all duration-700 ease-out" style={{
            fontSize: "clamp(2rem, 3.5vw, 3.25rem)",
            letterSpacing: "-0.022em",
            lineHeight: 1.04,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: "80ms"
          }}>
              Engineering{" "}
              <span className="italic transition-colors duration-[400ms] ease-out" style={{
              opacity: isVisible ? 1 : 0,
              transition: "opacity 200ms ease-out 800ms, color 400ms ease-out 1000ms",
              color: italicSettled ? "var(--foreground)" : "var(--accent-foreground)"
            }}>
                confidence
              </span>{" "}
              for hazardous industries.
            </h1>

            {}
            <p className="font-default mt-8 text-foreground/75 max-w-[60ch] transition-all duration-700 ease-out" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
            letterSpacing: "-0.005em",
            lineHeight: 1.6,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: "160ms"
          }}>Bedrock Safety Engineering provides fire protection engineering and risk assessment for large-scale sectors like aerospace, pharmaceutical, and industrial manufacturing</p>

            {}
            <div className="mt-10 flex flex-col items-start gap-4 transition-all duration-700 ease-out" style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: "240ms"
          }}>
              <Button asChild className="group relative h-[52px] px-7 rounded-lg bg-primary text-primary-foreground font-bold text-base hover:bg-accent-foreground transition-colors duration-200">
                <Link to="/ContactPage">
                  <span className="relative">
                    Request Technical Audit
                    <span className="pointer-events-none absolute left-0 -bottom-1 h-px w-0 bg-accent-foreground transition-all duration-[280ms] ease-out group-hover:w-full" />
                  </span>
                </Link>
              </Button>

              <Link to="/AboutPage" className="group inline-flex items-center gap-2 font-default text-foreground text-base pb-1 border-b border-foreground/40 hover:border-foreground transition-colors duration-200">
                <span>Our engineering philosophy</span>
                <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 lg:pt-2">
            <div className="flex flex-col">
              {proofPoints.map((point, i) => <div key={i} data-index={i} className={`py-6 transition-all duration-700 ease-out ${i !== 0 ? "border-t border-foreground/15" : ""}`} style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(16px)",
              transitionDelay: `${320 + i * 80}ms`
            }}>
                  <p className="font-mono text-xs tracking-[0.04em] text-foreground/60 mb-2">
                    {point.caption}
                  </p>
                  <h4 className="font-default font-semibold text-foreground" style={{
                fontSize: "clamp(1.0625rem, 1.2vw, 1.1875rem)",
                letterSpacing: "-0.008em",
                lineHeight: 1.35
              }}>
                    {point.title}
                  </h4>
                </div>)}
              <div className="border-t border-foreground/15" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}